// pages/Onboard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import ChatPanel from "../components/ChatPanel";
import LeftNav from "../components/LeftNav";
import { MessagesAPI } from "../api/messages";
import Loader from "../components/Loader";
import { getDomain, randomPassword, isBusinessEmail, onAxiosError, initSystemPrompt } from "../utils";


const stepFlow = {
  story: "srs",
  srs: "started",
  started: "success",
};

export default function Onboard() {
  const { step } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [history, setHistory] = useState([
    { role: 'model', text: initSystemPrompt, options: [] },
  ]);
  const [messages, setMessages] = useState([]);
  const [typeLoading, setTypeLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [writingLoading, setWritingLoading] = useState(false);

  const [signupErrors, setSignupErrors] = useState({});
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const abortRef = useRef(null);
  const scrollerRef = useRef(null);
  const hasSentInitialRef = useRef(false);

  const userId = useMemo(() => {
    return localStorage.getItem("visitorId") || null;
  }, []);

  useEffect(() => {
    if (!userId) navigate("/");
  }, [userId, navigate]);


  const loadMessages = useCallback(async (showLoader = true) => {
    if (showLoader) {
      setPageLoading(true);
    }
    try {
      const res = await MessagesAPI.getMessages({
        userId,
      });

      const formattedMessages = res.messages.map(msg => ({
        ...msg,
        text: msg.message,
        loaded: msg.loaded,
        options: msg?.details?.options || [],
        allow_srs_generation: msg?.details?.allow_srs_generation || false,
      }));

      setMessages(formattedMessages || []);
      setHistory(formattedMessages || []);
    } catch (error) {
      console.error("Get Messages: ", error);
      onAxiosError(error, () => { });
    } finally {
      if (showLoader) {
        setPageLoading(false);
      }
    }
  }, [userId]);

  const onSendMessage = useCallback(async (text) => {
    let prompt = (text ?? "").trim();
    if (!prompt || !userId) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    scrollerRef.current?.scrollToBottom({ behavior: "smooth" });
    setWritingLoading(true);
    setTypeLoading(true);
    setQuery("");

    setMessages((prev) => [...prev, { role: 'user', text: prompt, options: [] }]);
    setHistory((prev) => [...prev, { role: 'user', text: prompt, options: [] }]);

    try {
      const response = await MessagesAPI.generate(
        {
          prompt,
          history: history.map(msg => ({
            sender: msg.role === 'user' ? 'user' : 'model',
            text: msg.text,
            options: msg.options || [],
          })),
          userId,
        },
        { signal: controller.signal }
      );
      const text = response.text;
      const messageId = response.messageId;
      const options = response.options || [];
      const allow_srs_generation = response.allow_srs_generation || false;

      setMessages((prev) => [...prev, { id: messageId, role: 'assistant', text, options, loaded: 0, allow_srs_generation }]);
      setHistory((prev) => [...prev, { role: 'model', text, options }]);
      if (sessionStorage.getItem('initPrompt')) {
        sessionStorage.removeItem('initPrompt');
      }
    } catch (error) {
      console.error("Generate Message: ", error);
      onAxiosError(error, async () => {
        if (sessionStorage.getItem('initPrompt')) {
          prompt = sessionStorage.getItem('initPrompt') || "";
        }

        await loadMessages();
        onSendMessage(prompt);
      });
    } finally {
      setTypeLoading(false);
      abortRef.current = null;
    }
  }, [userId, history, step]);

  const loadSrs = useCallback(async () => {
    setPageLoading(true);
    try {
      const res = await MessagesAPI.fetchSrs({ userId });
      setMessages([...messages, { role: 'assistant', text: res?.srs, options: [] }]);
      setHistory([...history, { role: 'model', text: res?.srs, options: [] }]);
    } catch (error) {
      console.error("Get SRS: ", error);
      onAxiosError(error, () => { });
    } finally {
      setPageLoading(false);
    }
  }, [userId, messages, history]);


  useEffect(() => {
    const initialMessage = sessionStorage.getItem('initPrompt') || "";
    if (initialMessage && !hasSentInitialRef.current) {
      // sessionStorage.removeItem('initPrompt');
      onSendMessage(initialMessage);
      hasSentInitialRef.current = true;
    } else if (userId && !hasSentInitialRef.current && step === 'story') {
      loadMessages();
    } else if (userId && step === 'srs') {
      loadSrs();
    }
  }, [userId, step]);

  const onChangeInput = (e) => {
    const { name, value } = e.target;
    setSignupData({
      ...signupData,
      [name]: value,
    });
  };

  const validateSignup = () => {
    let tempErrors = {};
    if (!signupData.firstName) {
      tempErrors.firstName = 'First name is required.';
    }
    if (!signupData.lastName) {
      tempErrors.lastName = 'Last name is required.';
    }
    if (!signupData.email) {
      tempErrors.email = 'Email address is required.';
    }
    const { ok, reason } = isBusinessEmail(signupData.email);
    if (!ok) {
      tempErrors.email = reason;
    }
    setSignupErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const onPrevStep = (targetStep) => {
    navigate(`/onboard/${targetStep}`);
  };

  const onNextStep = async (e) => {
    const next = stepFlow[step];

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (step === 'story') {
      setPageLoading(true);
      setLoadingMessage("Generating SRS...");
      try {
        const res = await MessagesAPI.generateSrs(
          { userId },
          { signal: controller.signal }
        );
        setMessages([...messages, { role: 'assistant', text: res?.srs, options: [] }]);
        setHistory([...history, { role: 'model', text: res?.srs, options: [] }]);
        navigate(`/onboard/${next}`);
      } catch (error) {
        console.error("Generate SRS: ", error);
        onAxiosError(error, () => {
          onNextStep(e);
        });
      } finally {
        setPageLoading(false);
        setLoadingMessage("");
        abortRef.current = null;
      }
    }

    if (step === 'srs') {
      setPageLoading(true);
      try {
        // const srs = await MessagesAPI.fetchSrs({ userId });
        // setMessages([...messages, { role: 'assistant', text: srs, options: [] }]);
        // setHistory([...history, { role: 'model', text: srs, options: [] }]);
        navigate(`/onboard/${next}`);
      } catch (error) {
        console.error("Get SRS: ", error);
        onAxiosError(error, () => { });
      } finally {
        setPageLoading(false);
        abortRef.current = null;
      }
    }

    if (step === 'started') {
      e.preventDefault();

      if (validateSignup()) {
        setPageLoading(true);
        setLoadingMessage("Requesting for App building...");

        const password = randomPassword();
        const params = {
          password: password,
          onboarding_uid: userId,
          email: signupData.email,
          confirm_password: password,
          last_name: signupData.lastName,
          first_name: signupData.firstName,
          subdomain: getDomain(signupData.email),
        }
        try {
          const res = await MessagesAPI.buildApp(params, { signal: controller.signal });
          if (!res.data.success) {
            if (res?.error) {
              toast.error(res?.error);
            }

            setPageLoading(false);
            setLoadingMessage("");
          } else {
            setSignupData({
              firstName: '',
              lastName: '',
              email: '',
            });
            navigate(`/onboard/${next}`);
          }
        } catch (error) {
          console.error("Build App: ", error);
          onAxiosError(error, () => {
            onNextStep(e);
          });
        } finally {
          setPageLoading(false);
          setLoadingMessage("");
          abortRef.current = null;
        }
      }
    }
  };

  return (
    <>
      <Loader loading={pageLoading} text={loadingMessage} />

      <div className="step-wrapper">
        <div className="step-sidebar">
          <LeftNav step={step} />
        </div>

        <div className="step-content">
          <ChatPanel
            step={step}
            query={query}
            setQuery={setQuery}
            messages={messages}
            onNext={onNextStep}
            onPrev={onPrevStep}
            loading={typeLoading}
            onSend={onSendMessage}
            signupData={signupData}
            scrollerRef={scrollerRef}
            setMessages={setMessages}
            signupErrors={signupErrors}
            onChangeInput={onChangeInput}
            setWritingLoading={setWritingLoading}
          />
        </div>
      </div>
    </>
  );
}
